import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { Sheet } from './ui/Sheet';
import { Button } from './ui/Button';
import { Chip } from './ui/Chip';
import { RangeSlider } from './ui/RangeSlider';
import { DualRangeSlider } from './ui/DualRangeSlider';
import { SegmentedControl } from './ui/SegmentedControl';
import { Switch } from './ui/Switch';
import { eventTypes, genres } from '../data/options';
import { defaultFilters } from '../utils/event';
import { baht } from '../utils/format';
import { spring } from '../utils/motion';
import type { DateRange, EventFilters, EventType } from '../types/marketplace';
interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  filters: EventFilters;
  onApply: (filters: EventFilters) => void;
  countFor: (filters: EventFilters) => number;
}
const toggle = <T,>(list: T[], item: T) => list.includes(item) ? list.filter(x => x !== item) : [...list, item];
export function FilterSheet({
  open,
  onClose,
  filters,
  onApply,
  countFor
}: FilterSheetProps) {
  const [draft, setDraft] = useState<EventFilters>(filters);
  const [more, setMore] = useState(false);
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);
  const count = countFor(draft);
  const advancedCount = draft.types.length + draft.genres.length + (draft.multiActOnly ? 1 : 0);
  return <Sheet open={open} onClose={onClose} title="Filters" footer={<div className="flex items-center gap-3" data-id="element-279" data-mp="1upo8jx1g50if">
          <Button variant="ghost" onClick={() => setDraft(defaultFilters)} data-id="element-280" data-mp="1preddmfq1zwt">
            Reset
          </Button>
          <Button variant="primary" size="lg" fullWidth onClick={() => {
      onApply(draft);
      onClose();
    }} data-id="element-281" data-mp="1kst9nd19gy3m">
            Show{' '}
            <motion.span key={count} initial={{
        y: 8,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={spring.snappy} className="tabular inline-block" data-id="element-282" data-mp="3f7drlhy76fjb">
              {count}
            </motion.span>{' '}
            {count === 1 ? 'event' : 'events'}
          </Button>
        </div>} data-id="element-278" data-mp="1zr56pksjq8vb">
      <div className="space-y-7 pt-2" data-id="element-283" data-mp="13ka7e1q37cpe">
        <div data-id="element-284" data-mp="13h4h4j5q24s3">
          <p className="mb-3 text-[15px] font-bold" data-id="element-285" data-mp="18i21n2e7fawr">When</p>
          <SegmentedControl<DateRange> ariaLabel="Date range" layoutId="filter-date" size="sm" className="flex w-full" value={draft.dateRange} onChange={dateRange => setDraft({
          ...draft,
          dateRange
        })} options={[{
          value: 'any',
          label: 'Any time'
        }, {
          value: 'week',
          label: 'This week'
        }, {
          value: 'month',
          label: 'Month'
        }, {
          value: 'quarter',
          label: '3 months'
        }]} data-id="element-286" data-mp="18ilkclboi3bu" />
        </div>
        <RangeSlider label="Distance from you" min={5} max={200} step={5} value={draft.maxDistance} onChange={maxDistance => setDraft({
        ...draft,
        maxDistance
      })} format={v => v >= 200 ? 'Any distance' : `Within ${v} km`} data-id="element-287" data-mp="ft4vfxn9p4kd" />
        <DualRangeSlider label="Budget per act" min={0} max={100000} step={1000} value={draft.budget} onChange={budget => setDraft({
        ...draft,
        budget
      })} format={baht} data-id="element-288" data-mp="i8l0tobd2a7c" />

        <div className="border-t border-line pt-5" data-id="element-289" data-mp="3lp1l4rsxl295">
          <button type="button" onClick={() => setMore(m => !m)} aria-expanded={more} className="flex w-full items-center justify-between rounded-sm py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-290" data-mp="3gqgjqp1y0cj4">
            <span className="text-[15px] font-bold" data-id="element-291" data-mp="32xh6yutjowf9">
              More filters
              {advancedCount > 0 && <span className="ml-2 text-brand-strong" data-id="element-292" data-mp="2xzz5fphuxjha">{advancedCount} on</span>}
            </span>
            <motion.span animate={{
            rotate: more ? 180 : 0
          }} transition={spring.snappy} data-id="element-293" data-mp="218lorqyzeuki">
              <ChevronDownIcon className="h-5 w-5 text-ink-2" data-id="element-294" data-mp="258w0lk9ggf7a" />
            </motion.span>
          </button>
          <AnimatePresence initial={false} data-id="element-295" data-mp="2a88n23wezk9c">
            {more && <motion.div initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.18,
            ease: 'easeOut'
          }} className="space-y-6 pt-5" data-id="element-296" data-mp="2ez7b5pnn9mn5">
                <div data-id="element-297" data-mp="2syytg2l71p6y">
                  <p className="mb-3 text-sm font-bold text-ink-2" data-id="element-298" data-mp="2sygpskw7c8p0">Event type</p>
                  <div className="flex flex-wrap gap-2" data-id="element-299" data-mp="2xy2z4j8wt4sj">
                    {eventTypes.map(t => <Chip key={t} selected={draft.types.includes(t)} onClick={() => setDraft({
                  ...draft,
                  types: toggle<EventType>(draft.types, t)
                })} data-id="element-300" data-mp="yl7a2bd2bqyb">
                        {t}
                      </Chip>)}
                  </div>
                </div>
                <div data-id="element-301" data-mp="w2lz3ov47bdi">
                  <p className="mb-3 text-sm font-bold text-ink-2" data-id="element-302" data-mp="ra2pt43gn2ei">Genres</p>
                  <div className="flex flex-wrap gap-2" data-id="element-303" data-mp="r7zovp0rrov0">
                    {genres.map(g => <Chip key={g} selected={draft.genres.includes(g)} onClick={() => setDraft({
                  ...draft,
                  genres: toggle(draft.genres, g)
                })} data-id="element-304" data-mp="m8dbnfkxy8m7">
                        {g}
                      </Chip>)}
                  </div>
                </div>
                <Switch label="Multi-act line-ups only" description="Events booking more than one singer or band" checked={draft.multiActOnly} onChange={multiActOnly => setDraft({
              ...draft,
              multiActOnly
            })} data-id="element-305" data-mp="1a0tv9ukbu8nm" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </Sheet>;
}